import { rememberedCommand } from '@/features/consent-choices/consentCommands';
import { consentOwner } from '@/features/consent-choices/useConsentChoices';
import { ConsentChoices } from '@/features/consent-choices/ConsentChoices';
import { useState } from "react";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import {
  acceptTeamInvite,
  ApiError,
  getAuthMode,
  getTeamInviteDescriptor,
  login as apiLogin,
  logout as apiLogout,
  setAuthToken,
} from "@/lib/api";
import type { InviteConsents, InviteDescriptor } from "@/lib/types";
import { useMe } from "@/features/auth/useMe";
import { applyExplicitLogoutBoundary, unscopedKey } from "@/lib/queryClient";
import { Button } from "@/components/ui/Button";
import { authSearchParam, isLegacyRequested } from "@/features/auth/authPaths";
import {
  ConsentPanel,
  EMPTY_CONSENTS,
  requiredConsentsMet,
} from "@/features/onboarding/components/ConsentPanel";

/**
 * M9b E1–E7 — `/invite/{token}` invite landing.
 *
 * Private-beta rollout Phase 2: accepting now runs through an explicit
 * CONSENT step instead of auto-accepting. The page fetches the invite
 * descriptor (role, team name, tagged-player COUNT — never player names)
 * and renders unbundled checkboxes: the Beta Participation Agreement and
 * Privacy Notice for every acceptor, plus the per-player items (roster /
 * video / feedback required, photo optional) when the invite links
 * players. The server re-validates every flag on accept and 400s if a
 * required one is missing — the disabled button is UX, not enforcement.
 *
 * Two render branches:
 *   - Signed-in user: compact versioned choices + one "Confirm choices", POSTing
 *     exact token/selections to `/api/me/consent-choices`; historical receipt
 *     preserves joining and optional-choice outcomes. Explicit Continue routes
 *     to the server-validated destination. "Sign in as someone else" escape hatch kept.
 *   - Unauthenticated user: links to the in-app sign-in/register pages
 *     (Kratos flows render there), returning to this same invite URL —
 *     consent then happens in the signed-in branch. In legacy/dual mode
 *     the fallback form creates a local account; the same consent panel
 *     gates its submit.
 *
 * Post-accept routing by role (matches legacy `routeAfterInviteAcceptance`):
 *   - guardian / player → /feedback
 *   - team_admin       → /admin/people
 *   - coach / assistant_coach → /coach
 *   - anything else    → /  (defensive)
 *
 * Error states (E5) — backend statuses for the unhappy paths:
 *   - 404 → "That invite is invalid or expired."
 *   - 409 → "This invite has already been used."
 *   - 410 → "This invite has expired."
 *
 * Privacy invariant (E6): the URL token only moves from the path to the
 * sign-in `next` path, the descriptor POST body, or the accept mutation
 * body — never a query string, never logged. The legacy create-account
 * form clears `password` on submit so the value doesn't linger in React
 * state after a failed attempt.
 */
export function InviteAcceptPage() {
  const params = useParams({ from: "/invite/$token" });
  const search = useSearch({ from: "/invite/$token" });
  const token = decodeURIComponent(params.token);
  const me = useMe();
  const authMode = useQuery({
    queryKey: unscopedKey("auth-mode"),
    queryFn: getAuthMode,
    staleTime: 60_000,
  });
  const descriptor = useQuery({
    queryKey: unscopedKey("invite-descriptor", { token }),
    queryFn: () => getTeamInviteDescriptor(token),
    staleTime: 60_000,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && [404, 409, 410].includes(err.status))
        return false;
      return failureCount < 2;
    },
  });
  const kratosEnabled = authMode.data?.kratos_enabled ?? false;
  const legacyEnabled = authMode.data
    ? authMode.data.legacy_enabled
    : authMode.isError;
  const legacyRequested =
    isLegacyRequested(search.legacy) ||
    isLegacyRequested(authSearchParam("legacy"));

  if (me.isLoading || descriptor.isLoading) {
    return (
      <main
        data-testid="invite-loading"
        className="min-h-screen flex items-center justify-center text-ink-muted"
      >
        Loading…
      </main>
    );
  }

  // A dead invite (invalid / used / expired) renders the terminal error
  // without any form — nothing the visitor fills in can change it.
  const recoveringOriginal = !!me.data?.user && !!rememberedCommand(consentOwner() + ":" + token);
  if (descriptor.isError && !recoveringOriginal) {
    return (
      <main
        data-testid="invite-page"
        className="min-h-screen bg-canvas flex items-center justify-center p-4"
      >
        <section
          aria-label="Accept team invite"
          className="w-full max-w-md rounded border border-rule bg-surface p-6 flex flex-col gap-3"
        >
          <h1 className="text-xl font-display tracking-tight text-ink">
            You&rsquo;re invited
          </h1>
          <p
            data-testid="invite-descriptor-error"
            className="text-destructive text-xs font-mono"
          >
            {formatInviteError(descriptor.error)}
          </p>
          {isTerminalInviteError(descriptor.error) ? null : (
            // 404/409/410 are final — retrying an invalid, used or expired
            // invite just repeats the answer. Anything else (a network
            // blip, a 5xx during a deploy) is transient, and without this
            // the page told the invitee their good link was dead with no
            // way to find out otherwise short of a reload.
            <Button
              type="button"
              onClick={() => void descriptor.refetch()}
              disabled={descriptor.isFetching}
              data-testid="invite-descriptor-retry"
              className="self-start"
            >
              {descriptor.isFetching ? "Checking…" : "Try again"}
            </Button>
          )}
        </section>
      </main>
    );
  }

  const info = descriptor.data;

  return (
    <main
      data-testid="invite-page"
      className="min-h-screen bg-canvas flex items-center justify-center p-4"
    >
      <section
        aria-label="Accept team invite"
        className="w-full max-w-md rounded border border-rule bg-surface p-6 flex flex-col gap-4"
      >
        <header>
          <h1 className="text-xl font-display tracking-tight text-ink">
            You&rsquo;re invited
          </h1>
          <p data-testid="invite-descriptor-summary" className="text-ink-muted text-xs">
            {info
              ? `Join ${info.team_name} as ${roleLabel(info.role)}.`
              : "Create or use your account to join this team."}
          </p>
        </header>

        {me.data?.user ? (
          <SignedInBranch
            token={token}
            username={me.data.user.username}
            descriptor={info}
            authModeReady={!authMode.isPending}
          />
        ) : (
          <UnauthenticatedBranch
            token={token}
            descriptor={info}
            kratosEnabled={kratosEnabled}
            legacyEnabled={legacyEnabled}
            legacyRequested={legacyRequested}
            loadingMode={authMode.isPending}
          />
        )}
      </section>
    </main>
  );
}

// ---------------------------------------------------------------------------
// E2 — Signed-in branch
// ---------------------------------------------------------------------------

function SignedInBranch(props: { token: string; username: string; descriptor: InviteDescriptor | undefined; authModeReady: boolean }) {
  const navigate = useNavigate();
  return <div data-testid="invite-signed-in" className="space-y-3">
    <p className="text-sm text-ink-muted">Signed in as @{props.username}.</p>
    <Button variant="ghost" data-testid="invite-switch-account" disabled={!props.authModeReady} onClick={async () => {
      try { await apiLogout(); } catch { /* Existing logout boundary still retires local authority. */ }
      applyExplicitLogoutBoundary();
      void navigate({ to: '/login', search: { next: invitePathForToken(props.token) }, replace: true });
    }}>Sign in as someone else</Button>
    <ConsentChoices invitationToken={props.token}
      onContinue={receipt => { if (receipt.joined && receipt.destination) void navigate({ to: receipt.destination, replace: true }); }} />
  </div>;
}


function UnauthenticatedBranch({
  token,
  descriptor,
  kratosEnabled,
  legacyEnabled,
  legacyRequested,
  loadingMode,
}: {
  token: string;
  descriptor: InviteDescriptor | undefined;
  kratosEnabled: boolean;
  legacyEnabled: boolean;
  legacyRequested: boolean;
  loadingMode: boolean;
}) {
  const showLegacyCreate = legacyEnabled && (!kratosEnabled || legacyRequested);
  const showKratosEntry = kratosEnabled && !legacyRequested && !loadingMode;
  const next = invitePathForToken(token);

  return (
    <div className="flex flex-col gap-4">
      {loadingMode ? (
        <p
          data-testid="invite-auth-mode-loading"
          className="font-mono text-xs text-ink-muted"
        >
          Checking account options...
        </p>
      ) : null}
      {showKratosEntry ? (
        <KratosInviteBranch next={next} legacyEnabled={legacyEnabled} />
      ) : null}
      {showLegacyCreate ? (
        <CreateAccountBranch
          token={token}
          descriptor={descriptor}
          kratosEnabled={kratosEnabled}
        />
      ) : null}
    </div>
  );
}

function KratosInviteBranch({
  next,
  legacyEnabled,
}: {
  next: `/invite/${string}`;
  legacyEnabled: boolean;
}) {
  return (
    <section
      data-testid="invite-kratos-entry"
      className="rounded border border-rule bg-canvas p-4"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-accent text-accent-ink">
          <ShieldCheck aria-hidden="true" className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <h2 className="font-display text-base font-semibold text-ink">
            Sign in to accept
          </h2>
          <p className="mt-1 text-xs leading-5 text-ink-muted">
            Sign in or create your account, then Tktic brings you back to
            this invite to confirm consent.
          </p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button asChild size="sm" data-testid="invite-kratos-login">
          <Link to="/login" search={{ next }}>
            Sign in
          </Link>
        </Button>
        <Button asChild size="sm" variant="outline" data-testid="invite-kratos-register">
          <Link to="/register" search={{ next }}>
            Create account
          </Link>
        </Button>
      </div>
      {legacyEnabled ? (
        <a
          href={`${next}?legacy=1`}
          data-testid="invite-legacy-link"
          className="mt-3 inline-block rounded px-1 py-0.5 text-left font-mono text-[0.65rem] text-ink-muted hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          Use legacy account form
        </a>
      ) : null}
    </section>
  );
}

function CreateAccountBranch({
  token,
  descriptor,
  kratosEnabled,
}: {
  token: string;
  descriptor: InviteDescriptor | undefined;
  kratosEnabled: boolean;
}) {
  const navigate = useNavigate({ from: "/invite/$token" });
  const queryClient = useQueryClient();

  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [consents, setConsents] = useState<InviteConsents>(EMPTY_CONSENTS);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const next = invitePathForToken(token);
  void kratosEnabled;
  const signInPath = `/login?next=${encodeURIComponent(next)}`;

  const consentsReady =
    descriptor !== undefined &&
    requiredConsentsMet(consents, descriptor.parental_player_count);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSuccess(null);
    const u = username.trim();
    if (!u) {
      setError("Username is required.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (!consentsReady) {
      setError("Please confirm the required consent items.");
      return;
    }
    setSubmitting(true);
    // Capture the password locally then clear React state before
    // the network calls — the on-screen field clears immediately
    // and the in-memory value lives only for the duration of the
    // two API calls below (E6 privacy invariant).
    const pw = password;
    setPassword("");
    try {
      const acceptResp = await acceptTeamInvite({
        token,
        username: u,
        password: pw,
        display_name: displayName.trim() || undefined,
        consents,
      });
      setSuccess("Account created. Signing you in…");
      // Auto-login with the new credentials so the user lands
      // authenticated. If this fails for any reason, fall back to
      // the manual sign-in path (the account exists, the operator
      // just needs to authenticate explicitly).
      try {
        const loginResp = await apiLogin({ username: u, password: pw });
        setAuthToken(loginResp.token);
        await queryClient.invalidateQueries();
        window.setTimeout(() => {
          void navigate({
            to: "/assist-choice",
            search: { team: acceptResp.membership.team_id, next: routeForRole(acceptResp.membership.role) },
            replace: true,
          });
        }, 600);
      } catch {
        setError(
          "Account created, but automatic sign-in failed. Sign in with your new account to continue.",
        );
        window.setTimeout(() => {
          void navigate({ to: "/login", search: { next: `/assist-choice?${new URLSearchParams({ team: acceptResp.membership.team_id, next: routeForRole(acceptResp.membership.role) })}` }, replace: true });
        }, 1500);
      }
    } catch (err) {
      setError(formatInviteError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      data-testid="invite-create-account"
      onSubmit={handleSubmit}
      noValidate
      className="flex flex-col gap-3"
    >
      <p className="text-sm text-ink-muted">
        Create an account and join the team with this invite. Already have one?{" "}
        <a
          href={signInPath}
          data-testid="invite-sign-in-instead"
          className="text-accent hover:underline"
        >
          Sign in instead
        </a>
        .
      </p>

      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Username
        </span>
        <input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoFocus
          required
          minLength={1}
          maxLength={100}
          autoComplete="username"
          data-testid="invite-create-username"
          className="rounded border border-rule bg-canvas px-2 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
      </label>
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Display name
        </span>
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={200}
          autoComplete="name"
          data-testid="invite-create-display-name"
          className="rounded border border-rule bg-canvas px-2 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
        <span className="text-[0.6rem] font-mono text-ink-muted">
          Optional. Visible to your teammates.
        </span>
      </label>
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Choose a password
        </span>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={8}
          maxLength={200}
          autoComplete="new-password"
          data-testid="invite-create-password"
          className="rounded border border-rule bg-canvas px-2 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
        <span className="text-[0.6rem] font-mono text-ink-muted">
          At least 8 characters.
        </span>
      </label>

      {descriptor ? (
        <ConsentPanel
          playerCount={descriptor.parental_player_count}
          consents={consents}
          onChange={setConsents}
        />
      ) : null}

      {error ? (
        <p
          data-testid="invite-create-error"
          className="text-destructive text-xs font-mono"
        >
          {error}
        </p>
      ) : null}
      {success ? (
        <p
          data-testid="invite-create-success"
          className="text-good text-xs font-mono"
        >
          {success}
        </p>
      ) : null}

      <Button
        type="submit"
        disabled={submitting || !consentsReady}
        data-testid="invite-create-submit"
        className="self-start"
      >
        {submitting ? "Creating…" : "Create account and join team"}
      </Button>
    </form>
  );
}

function invitePathForToken(token: string): `/invite/${string}` {
  return `/invite/${encodeURIComponent(token)}`;
}

// ---------------------------------------------------------------------------
// E4 — role → destination
// ---------------------------------------------------------------------------

function roleLabel(role: string): string {
  const normalized = (role || "").toLowerCase();
  if (normalized === "guardian") return "a parent/guardian";
  if (normalized === "player") return "a player";
  if (normalized === "team_admin") return "a team admin";
  if (normalized === "coach") return "a coach";
  if (normalized === "assistant_coach") return "an assistant coach";
  if (normalized === "viewer") return "a viewer";
  return `a ${normalized || "member"}`;
}

function routeForRole(
  role: string,
): "/feedback" | "/admin/people" | "/coach" | "/" {
  const normalized = (role || "").toLowerCase();
  if (normalized === "guardian" || normalized === "player") return "/feedback";
  if (normalized === "team_admin") return "/admin/people";
  if (normalized === "coach" || normalized === "assistant_coach")
    return "/coach";
  return "/";
}

// ---------------------------------------------------------------------------
// E5 — error mapping
// ---------------------------------------------------------------------------

/** 404 invalid · 409 already used · 410 expired — the invite is genuinely
 * dead and no amount of retrying changes that. */
function isTerminalInviteError(err: unknown): boolean {
  return err instanceof ApiError && [404, 409, 410].includes(err.status);
}

function formatInviteError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 404) return "That invite is invalid or expired.";
    if (err.status === 409) return "This invite has already been used.";
    if (err.status === 410) return "This invite has expired.";
    return typeof err.detail === "string"
      ? err.detail
      : `Could not accept the invite (HTTP ${err.status}).`;
  }
  return "Network error. Try again in a moment.";
}
